<template>
  <v-dialog v-model="dialog" max-width="900" scrollable>
    <v-card>
      <v-card-title class="headline">
        <v-icon left>fal fa-paper-plane</v-icon>
        Compose Push Mail
      </v-card-title>

      <v-card-text>
        <v-stepper v-model="step" vertical>

          <!-- STEP 1: AUDIENCE & FILTERS -->
          <v-stepper-step :complete="step > 1" step="1">
            Audience & Filters
          </v-stepper-step>
          <v-stepper-content step="1">
            <v-row>
              <!-- Recipient type -->
              <v-col cols="12">
                <v-select
                  v-model="form.filters.recipient_type"
                  :items="recipientTypes"
                  label="Send to"
                  outlined dense
                />
              </v-col>

              <!-- Country -->
              <v-col v-if="includesInfluencers" cols="12" md="6">
                <v-select
                  v-model="form.filters.country_codes"
                  :items="countries"
                  item-text="name"
                  item-value="code"
                  label="Countries"
                  multiple chips deletable-chips
                  outlined dense clearable
                />
              </v-col>

              <!-- Search by name/email -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.filters.search"
                  label="Search by name or email"
                  outlined dense clearable
                />
              </v-col>

              <!-- INFLUENCER FILTERS (shown when type includes influencers) -->
              <template v-if="includesInfluencers">
                <v-col cols="12">
                  <v-subheader class="pl-0">Influencer Filters</v-subheader>
                </v-col>

                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="form.filters.min_followers"
                    label="Min followers"
                    type="number" outlined dense clearable
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="form.filters.max_followers"
                    label="Max followers"
                    type="number" outlined dense clearable
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="form.filters.min_reach"
                    label="Min reach"
                    type="number" outlined dense clearable
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="form.filters.min_views"
                    label="Min views"
                    type="number" outlined dense clearable
                  />
                </v-col>

                <v-col cols="6" md="3">
                  <v-select
                    v-model="form.filters.gender"
                    :items="genders"
                    label="Gender"
                    outlined dense clearable
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-select
                    v-model="form.filters.channel_type"
                    :items="channelTypes"
                    label="Platform"
                    outlined dense clearable
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="form.filters.min_age"
                    label="Min age"
                    type="number" outlined dense clearable
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="form.filters.max_age"
                    label="Max age"
                    type="number" outlined dense clearable
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.filters.influencer_status"
                    :items="influencerStatuses"
                    label="Influencer status"
                    multiple chips deletable-chips
                    outlined dense clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.filters.category_ids"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Content categories"
                    multiple chips deletable-chips
                    outlined dense clearable
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="form.filters.approved"
                    label="Only influencers with approved channels"
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.filters.no_campaigns_since_days"
                    label="Dormant — no campaigns in last X days"
                    type="number" outlined dense clearable
                    hint="Leave empty to include all"
                    persistent-hint
                  />
                </v-col>
              </template>

              <!-- BUSINESS FILTERS (shown when type includes businesses) -->
              <template v-if="includesBusinesses">
                <v-col cols="12">
                  <v-subheader class="pl-0">Business Filters</v-subheader>
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                    v-model="form.filters.has_pixel_approved"
                    :items="[{ text: 'Pixel approved (active)', value: true }, { text: 'Not approved', value: false }]"
                    label="Business status"
                    outlined dense clearable
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="form.filters.has_active_subscription"
                    label="Has active subscription"
                    dense
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="form.filters.has_active_campaigns"
                    label="Has active campaigns"
                    dense
                  />
                </v-col>
              </template>

              <!-- REGISTRATION DATE RANGE (shared) -->
              <v-col cols="12">
                <v-subheader class="pl-0">Registration date range</v-subheader>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="form.filters.registered_after"
                  label="Registered after"
                  type="date" outlined dense clearable
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="form.filters.registered_before"
                  label="Registered before"
                  type="date" outlined dense clearable
                />
              </v-col>
            </v-row>

            <!-- Preview recipient count -->
            <v-row>
              <v-col cols="12">
                <v-btn
                  color="primary" outlined
                  :loading="previewing"
                  @click="previewRecipients"
                >
                  <v-icon left small>fal fa-users</v-icon>
                  Preview recipients
                </v-btn>

                <v-alert
                  v-if="preview !== null"
                  type="info" dense text class="mt-3"
                >
                  <strong>{{ preview.recipient_count }}</strong> recipients match these filters.
                  <span v-if="preview.sample_emails && preview.sample_emails.length">
                    Sample: {{ preview.sample_emails.join(', ') }}
                  </span>
                </v-alert>
              </v-col>
            </v-row>

            <v-btn
              color="primary" class="mt-3"
              :disabled="!form.filters.recipient_type"
              @click="step = 2"
            >
              Next
            </v-btn>
          </v-stepper-content>

          <!-- STEP 2: EMAIL CONTENT -->
          <v-stepper-step :complete="step > 2" step="2">
            Email Content
          </v-stepper-step>
          <v-stepper-content step="2">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.subject"
                  label="Email subject *"
                  outlined dense
                  :rules="[v => !!v || 'Required']"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.body_content"
                  label="Email body (HTML supported) *"
                  outlined
                  rows="12"
                  :hint="'This is injected as the {{body_content}} merge tag in the Mandrill template.'"
                  persistent-hint
                  :rules="[v => !!v || 'Required']"
                />
              </v-col>
            </v-row>

            <v-btn text class="mt-3 mr-2" @click="step = 1">Back</v-btn>
            <v-btn
              color="primary" class="mt-3"
              :disabled="!form.subject || !form.body_content"
              @click="step = 3"
            >
              Next
            </v-btn>
          </v-stepper-content>

          <!-- STEP 3: CONFIRM & SEND -->
          <v-stepper-step step="3">
            Review & Send
          </v-stepper-step>
          <v-stepper-content step="3">
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr>
                  <td><strong>Send to</strong></td>
                  <td>{{ recipientTypeLabel }}</td>
                </tr>
                <tr v-if="preview">
                  <td><strong>Estimated recipients</strong></td>
                  <td>{{ preview.recipient_count }}</td>
                </tr>
                <tr>
                  <td><strong>Subject</strong></td>
                  <td>{{ form.subject }}</td>
                </tr>
                <tr>
                  <td><strong>Countries</strong></td>
                  <td>{{ (form.filters.country_codes || []).join(', ') || 'All' }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-alert type="warning" dense text>
              This will send a real email to every matched recipient. This action cannot be undone.
            </v-alert>

            <v-btn text class="mt-3 mr-2" @click="step = 2">Back</v-btn>
            <v-btn
              color="error" class="mt-3"
              :loading="sending"
              @click="send"
            >
              <v-icon left small>fal fa-paper-plane</v-icon>
              Send Push Mail
            </v-btn>
          </v-stepper-content>

        </v-stepper>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "PushMailComposeDialog",

  props: {
    value: { type: Boolean, default: false },
  },

  data: () => ({
    step: 1,
    previewing: false,
    sending: false,
    preview: null,

    form: {
      template_id: "admin-push-mail",
      subject:      "",
      body_content: "",
      filters: {
        recipient_type: "influencers",
        country_codes:  [],
        search: "",
        // influencer
        min_followers:           null,
        max_followers:           null,
        min_reach:               null,
        min_views:               null,
        gender:                  null,
        channel_type:            null,
        min_age:                 null,
        max_age:                 null,
        influencer_status:       [],
        category_ids:            [],
        approved:                false,
        has_active_channels:     false,
        no_campaigns_since_days: null,
        // business
        has_pixel_approved:       null,
        has_active_subscription:  false,
        has_active_campaigns:     false,
        // shared
        registered_after:  null,
        registered_before: null,
      },
    },

    recipientTypes: [
      { text: "All Influencers",            value: "influencers" },
      { text: "All Businesses",             value: "businesses"  },
      { text: "Influencers & Businesses",   value: "both"        },
    ],

    genders: [
      { text: "Male",   value: "male"   },
      { text: "Female", value: "female" },
      { text: "Other",  value: "other"  },
    ],

    channelTypes: [
      { text: "Instagram", value: "instagram" },
      { text: "TikTok",    value: "tiktok"    },
      { text: "YouTube",   value: "youtube"   },
    ],

    influencerStatuses: [
      { text: "Active",         value: 1 },
      { text: "Hidden",         value: 2 },
      { text: "Hidden (system)",value: 3 },
    ],

    countries: [
      { name: "Denmark",     code: "DK" },
      { name: "Sweden",      code: "SE" },
      { name: "Norway",      code: "NO" },
      { name: "Finland",     code: "FI" },
      { name: "Germany",     code: "DE" },
      { name: "Netherlands", code: "NL" },
      // extend this list or load from API
    ],

    categories: [], // load from API if needed
  }),

  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    includesInfluencers() {
      return ["influencers", "both"].includes(this.form.filters.recipient_type);
    },
    includesBusinesses() {
      return ["businesses", "both"].includes(this.form.filters.recipient_type);
    },
    recipientTypeLabel() {
      return this.recipientTypes.find(r => r.value === this.form.filters.recipient_type)?.text ?? "";
    },
  },

  methods: {
    ...mapActions("moderator", [
      "sendAdminPushMail",
      "previewAdminPushMailRecipients",
    ]),

    async previewRecipients() {
      this.previewing = true;
      try {
        this.preview = await this.previewAdminPushMailRecipients({
          filters: this.cleanFilters(),
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.previewing = false;
      }
    },

    async send() {
      this.sending = true;
      try {
        const result = await this.sendAdminPushMail({
          template_id:  this.form.template_id,
          subject:      this.form.subject,
          body_content: this.form.body_content,
          filters:      this.cleanFilters(),
        });

        if (result.success) {
          this.$emit("sent", result);
          this.close();
        } else {
          alert(result.message || "No recipients matched the filters.");
        }
      } catch (e) {
        console.error(e);
        alert("An error occurred while sending. Check the logs.");
      } finally {
        this.sending = false;
      }
    },

    // Strip null/empty values before sending to API
    cleanFilters() {
      const f = { ...this.form.filters };
      Object.keys(f).forEach(k => {
        if (f[k] === null || f[k] === "" || (Array.isArray(f[k]) && f[k].length === 0)) {
          delete f[k];
        }
      });
      return f;
    },

    close() {
      this.dialog = false;
      this.step    = 1;
      this.preview = null;
    },
  },
};
</script>